
import React, { useState, useEffect } from 'react';
import { BASE } from '../utils/api';
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import { Edit as EditIcon, Refresh as RefreshIcon } from '@mui/icons-material';

interface ExchangeRate {
  currency: string;
  usd_rate: number;
  last_updated: string;
}

const PRECISION = 6;

const panelSx = {
  backgroundColor: '#232626',
  border: '1px solid #2f3536',
  borderRadius: 3,
  boxShadow: '0 18px 42px rgba(0, 0, 0, 0.28)',
};

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: '#e2e8f0',
    backgroundColor: '#1a1d1e',
    borderRadius: '12px',
    '& fieldset': {
      borderColor: '#2f3536',
    },
    '&:hover fieldset': {
      borderColor: '#25ee89',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#25ee89',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#94a3b8',
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#25ee89',
  },
};

const roundToFixed = (num: number, decimals: number): number => {
  return Number(Math.round(Number(num + 'e' + decimals)) + 'e-' + decimals);
};

const ExchangeRate: React.FC = () => {
  const [rates, setRates] = useState<ExchangeRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editDialog, setEditDialog] = useState(false);
  const [selectedCurrency, setSelectedCurrency] = useState<ExchangeRate | null>(null);
  const [newRate, setNewRate] = useState<string>('');
  const [updateLoading, setUpdateLoading] = useState(false);

  const fetchRates = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE}/exchangeRate/rates`);
      const data = await response.json();
      if (data.success) {
        setRates(data.data);
      } else {
        setError('Failed to fetch exchange rates');
      }
    } catch (err) {
      setError('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRates();
  }, []);

  const handleEditClick = (rate: ExchangeRate) => {
    setSelectedCurrency(rate);
    setNewRate((1 / rate.usd_rate).toFixed(PRECISION));
    setEditDialog(true);
  };

  const handleUpdate = async () => {
    if (!selectedCurrency || !newRate) return;

    try {
      setUpdateLoading(true);
      const directRate = parseFloat(newRate);
      const usdRate = roundToFixed(1 / directRate, PRECISION);
      
      const response = await fetch(`${BASE}/exchangeRate/rates/${selectedCurrency.currency}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          usd_rate: usdRate
        }),
      });

      const data = await response.json();
      if (data.success) {
        await fetchRates();
        setEditDialog(false);
      } else {
        setError('Failed to update rate');
      }
    } catch (err) {
      setError('Error updating rate');
    } finally {
      setUpdateLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="ml-0 lg:ml-[256px] min-h-screen bg-[#1a1d1e] flex items-center justify-center p-4">
        <CircularProgress sx={{ color: '#25ee89' }} />
      </div>
    );
  }

  return (
    <div className="ml-0 lg:ml-[256px] min-h-screen bg-[#1a1d1e] p-4 md:p-6">
      <div>
        <Card sx={panelSx}>
          <CardHeader
            sx={{ pb: 0 }}
            title={
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <Typography variant="h4" component="h2" sx={{ color: '#e2e8f0', fontWeight: 700 }}>
                  Exchange Rates
                </Typography>
                <Button
                  onClick={fetchRates}
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  sx={{
                    backgroundColor: '#25ee89',
                    color: '#1a1d1e',
                    borderRadius: '12px',
                    px: 2.5,
                    py: 1.25,
                    fontWeight: 700,
                    boxShadow: 'none',
                    '&:hover': {
                      backgroundColor: '#1fcc76',
                      boxShadow: 'none',
                    },
                  }}
                >
                  Refresh
                </Button>
              </div>
            }
          />
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            {error && (
              <Alert
                severity="error"
                sx={{
                  marginBottom: '1rem',
                  backgroundColor: '#3a1f1f',
                  color: '#fecaca',
                  border: '1px solid #7f1d1d',
                  '& .MuiAlert-icon': { color: '#f87171' },
                }}
              >
                {error}
              </Alert>
            )}
            
            <TableContainer
              component={Paper}
              sx={{
                backgroundColor: '#232626',
                border: '1px solid #2f3536',
                borderRadius: 3,
                overflow: 'hidden',
              }}
            >
              <Table>
                <TableHead sx={{ backgroundColor: '#1fcc76' }}>
                  <TableRow>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 700, borderBottomColor: '#1fcc76' }}>Currency</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 700, borderBottomColor: '#1fcc76' }}>1 USD Rate</TableCell>
                    <TableCell sx={{ color: '#ffffff', fontWeight: 700, borderBottomColor: '#1fcc76' }}>Currency Rate</TableCell>
                    <TableCell align="right" sx={{ color: '#ffffff', fontWeight: 700, borderBottomColor: '#1fcc76' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rates.map((rate) => (
                    <TableRow
                      key={rate.currency}
                      hover
                      sx={{
                        backgroundColor: '#232626',
                        '&:hover': { backgroundColor: '#1f2324' },
                        '& td, & th': {
                          borderBottomColor: '#2f3536',
                          color: '#94a3b8',
                        },
                      }}
                    >
                      <TableCell component="th" scope="row" sx={{ color: '#e2e8f0 !important', fontWeight: 600 }}>
                        {rate.currency}
                      </TableCell>
                      <TableCell>
                        1 USD = {(1 / rate.usd_rate).toFixed(PRECISION)} {rate.currency}
                      </TableCell>
                      <TableCell>
                        1 {rate.currency} = {rate.usd_rate.toFixed(PRECISION)} USD
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleEditClick(rate)}
                          sx={{
                            color: '#25ee89',
                            border: '1px solid #2f3536',
                            borderRadius: '10px',
                            '&:hover': {
                              backgroundColor: 'rgba(37, 238, 137, 0.08)',
                              borderColor: '#25ee89',
                            },
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        <Dialog
          open={editDialog}
          onClose={() => setEditDialog(false)}
          PaperProps={{
            sx: {
              backgroundColor: '#232626',
              color: '#e2e8f0',
              border: '1px solid #2f3536',
              borderRadius: 3,
              minWidth: { xs: 'auto', sm: 520 },
            },
          }}
        >
          <DialogTitle sx={{ color: '#e2e8f0', fontWeight: 700 }}>
            Update {selectedCurrency?.currency} Exchange Rate
          </DialogTitle>
          <DialogContent>
            <div style={{ paddingTop: '1rem' }}>
              <TextField
                label={`1 USD equals how many ${selectedCurrency?.currency}?`}
                type="number"
                value={newRate}
                onChange={(e) => {
                  const value = e.target.value;
                  setNewRate(value);
                }}
                fullWidth
                inputProps={{ step: "0.000001" }}
                sx={fieldSx}
              />
              {selectedCurrency && newRate && !isNaN(parseFloat(newRate)) && (
                <Typography variant="body2" sx={{ marginTop: '1rem', color: '#94a3b8' }}>
                  Current rate: 1 USD = {(1 / selectedCurrency.usd_rate).toFixed(PRECISION)} {selectedCurrency.currency}
                  <br />
                  New rate: 1 {selectedCurrency.currency} = {(1 / parseFloat(newRate)).toFixed(PRECISION)} USD
                </Typography>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setEditDialog(false)}
              sx={{ color: '#94a3b8', fontWeight: 700 }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUpdate} 
              disabled={updateLoading}
              variant="contained"
              sx={{
                backgroundColor: '#25ee89',
                color: '#1a1d1e',
                fontWeight: 700,
                borderRadius: '10px',
                '&:hover': {
                  backgroundColor: '#1fcc76',
                },
                '&.Mui-disabled': {
                  backgroundColor: '#2f3536',
                  color: '#64748b',
                },
              }}
            >
              {updateLoading ? (
                <>
                  <CircularProgress size={20} sx={{ marginRight: '0.5rem', color: '#1a1d1e' }} />
                  Updating...
                </>
              ) : (
                'Update Rate'
              )}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default ExchangeRate;
